// A module's home page (/m/<slug>): the user's projects in the module, "New project" and its illustrative projects,
// what the module does and the questions it answers, what it works with, and its sub-modules (Properties: its families,
// from the server's registry, with the ones still to come).
import { Anchor, Badge, Box, Button, Card, Group, List, SimpleGrid, Stack, Table, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useParams } from "react-router";
import { JobStatusBadge, useKindLabel } from "../components/Jobs";
import { Crumbs, ModuleBadge, Tag } from "../components/Modules";
import { formatDate, get, type Job, type Project } from "../lib/api";
import { useExamples } from "../lib/examples";
import { examplesPath, moduleBySlug, moduleProducts, modulePath, type ModuleInfo, type SubModule } from "../lib/modules";
import { propertiesRegistry } from "../lib/properties";
import { NewProjectButton } from "./Projects";

/** The Properties families not installed yet: what they will hold. */
const COMING_FAMILIES: Record<string, string[]> = {
  boltzmann: ["Conductivity", "Seebeck", "Hall"],
  optical: ["Optical conductivity", "Circular dichroism"],
  magnetic: ["Orbital magnetisation", "g-factors"],
};

function SubCard({ s, color }: { s: SubModule; color: string }) {
  return (
    <Card withBorder p="sm" style={{ opacity: s.coming ? 0.75 : 1 }} data-testid="submodule">
      <Group justify="space-between" wrap="nowrap" align="flex-start" mb={6}>
        <Text fw={600} size="sm" style={{ color: s.coming ? undefined : color }}>{s.title}</Text>
        <Badge size="sm" variant="light" color={s.coming ? "gray" : "green"} style={{ textTransform: "none", flexShrink: 0 }}>
          {s.coming ? "coming" : "available"}
        </Badge>
      </Group>
      <div>{s.items.map((t) => <Tag key={t} coming={s.coming}>{t}</Tag>)}</div>
    </Card>
  );
}

/** Properties: the families of the server's registry, their calculators, and those still to come. */
function useSubModules(m: ModuleInfo): SubModule[] | undefined {
  const reg = useQuery({ queryKey: ["properties-registry"], queryFn: () => propertiesRegistry(), enabled: m.slug === "properties",
    staleTime: 60_000 });
  if (m.slug !== "properties") return m.subs;
  if (!reg.data) return undefined;
  return reg.data.families.map((f) => ({
    title: f.title,
    coming: !f.installed,
    items: f.installed ? reg.data!.calculators.filter((c) => c.family === f.id).map((c) => c.title) : (COMING_FAMILIES[f.id] ?? []),
  }));
}

export function ModuleHome() {
  const { slug } = useParams();
  const m = moduleBySlug(slug);
  const projects = useQuery({ queryKey: ["projects"], queryFn: () => get<Project[]>("/projects"), enabled: !!m?.product });
  const jobs = useQuery({ queryKey: ["jobs", "home"], queryFn: () => get<Job[]>("/jobs?limit=50"), enabled: !!m?.product });
  const examples = useExamples();
  const label = useKindLabel();
  const subs = useSubModules(m ?? ({} as ModuleInfo));
  const wide = useMediaQuery("(min-width: 62em)");
  if (!m) return <Navigate to="/" replace />;

  const products = moduleProducts(m);
  const mine = (projects.data ?? []).filter((p) => products.includes(p.product)).sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  const gallery = m.examplesProduct
    ? (examples.data ?? []).filter((e) => [m.examplesProduct, ...products.slice(1)].includes(e.product ?? "skfitter"))
      .sort((a, b) => (a.product === b.product ? 0 : a.product === m.examplesProduct ? -1 : 1) || a.order - b.order) : [];
  const lastJob = (p: Project) => (jobs.data ?? []).find((j) => j.project_id === p.id);
  const ins = m.worksWith.filter((w) => w.dir === "in"), outs = m.worksWith.filter((w) => w.dir === "out");
  const ww = (w: ModuleInfo["worksWith"][number]) => (
    <Text size="sm" key={w.label}>
      {w.dir === "in" ? "← " : "→ "}
      {w.slug ? <Anchor component={Link} to={modulePath(w.slug)} size="sm">{w.label}</Anchor> : <b style={{ fontWeight: 500 }}>{w.label}</b>}
      {w.note && <Text span c="dimmed" size="sm"> {w.note}</Text>}
    </Text>
  );

  return (
    <Stack gap="md" data-testid={`module-home-${m.slug}`}>
      <div>
        <Crumbs items={[{ label: "Home", to: "/" }, { label: m.name }]} />
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Group gap="md" wrap="nowrap">
            <div style={{ width: 46, height: 46, borderRadius: 10, background: m.tint, color: m.color, display: "flex",
              alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 20 }} aria-hidden>{m.icon}</div>
            <div>
              <Text component="h1" fw={700} fz={24} m={0}>{m.name}</Text>
              <Text c="dimmed" size="sm">{m.subtitle}</Text>
            </div>
          </Group>
          <Group gap="xs">
            {m.examplesProduct && (
              <Button variant="light" component={Link} to={examplesPath(m.slug)}>Illustrative projects ({gallery.length})</Button>
            )}
            {m.start ? <Button component={Link} to={m.start.to}>{m.start.label}</Button>
              : m.product && <NewProjectButton product={m.product} label={`+ New ${m.subProducts?.length ? m.subtitle : m.name} project`} />}
            {(m.subProducts ?? []).map((s) => <NewProjectButton key={s.product} product={s.product} label={`+ New ${s.name} project`} />)}
          </Group>
        </Group>
      </div>

      {m.product && (
        <Card withBorder>
          <Group justify="space-between" mb="xs">
            <Text fw={600}>My {m.name} projects</Text>
            <Text size="xs" c="dimmed">{mine.length} project{mine.length === 1 ? "" : "s"}</Text>
          </Group>
          {!mine.length ? (
            <Text size="sm" c="dimmed">
              No {m.name} projects yet. Start one with the button above{m.examplesProduct ? ", or open an illustrative project below as your own copy" : ""}.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={480}>
              <Table verticalSpacing={6} highlightOnHover data-testid="module-projects">
                <Table.Thead><Table.Tr><Table.Th>Project</Table.Th><Table.Th>Last calculation</Table.Th><Table.Th>Created</Table.Th><Table.Th>Updated</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                  {mine.map((p) => {
                    const j = lastJob(p);
                    return (
                      <Table.Tr key={p.id}>
                        <Table.Td><Group gap={6} wrap="nowrap"><Anchor component={Link} to={`/projects/${p.id}`} size="sm">{p.name}</Anchor>
                          {m.subProducts?.length ? <ModuleBadge product={p.product} /> : null}</Group></Table.Td>
                        <Table.Td>{j ? <Group gap={6} wrap="nowrap"><Text size="sm">{label(j.kind)}</Text><JobStatusBadge status={j.status} /></Group> : <Text size="sm" c="dimmed">-</Text>}</Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{formatDate(p.created_at)}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{formatDate(p.updated_at)}</Text></Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Card>
      )}

      <Box style={{ display: "grid", gap: 16, alignItems: "start",
        gridTemplateColumns: wide && m.worksWith.length ? "1.55fr 1fr" : "1fr" }}>
        <Card withBorder>
          <Text fw={600} mb={6}>What you can do here</Text>
          <Text size="sm" lh={1.55}>{m.about}</Text>
          <Text fw={600} mt="md" mb={6}>Questions you can answer</Text>
          <List size="sm" spacing={4} data-testid="module-questions">{m.questions.map((q) => <List.Item key={q}>{q}</List.Item>)}</List>
        </Card>
        {!!m.worksWith.length && (
          <Card withBorder>
            <Text fw={600} mb={6}>Works with</Text>
            <Stack gap={4}>{ins.map(ww)}{outs.map(ww)}</Stack>
            {m.slug === "properties" && (
              <>
                <Text fw={600} mt="md" mb={6}>Engines</Text>
                <Text size="sm">Native (reference) · <b style={{ fontWeight: 500 }}>WannierBerri</b> <Text span c="dimmed" size="sm">cross-check</Text></Text>
              </>
            )}
          </Card>
        )}
      </Box>

      {subs && !!subs.length && (
        <div>
          <Group justify="space-between" mb="xs">
            <Text fw={600}>{m.subsTitle ?? "Sub-modules"}</Text>
            {subs.some((s) => s.coming) && (
              <Text size="xs" c="dimmed">{subs.filter((s) => !s.coming).length} of {subs.length} available · more to come</Text>
            )}
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: Math.min(subs.length, 5) }} spacing="sm">
            {subs.map((s) => <SubCard key={s.title} s={s} color={m.color} />)}
          </SimpleGrid>
        </div>
      )}

      {m.examplesProduct ? (
        <div>
          <Group justify="space-between" mb="xs">
            <Text fw={600}>Illustrative projects</Text>
            <Anchor component={Link} to={examplesPath(m.slug)} size="sm">See all {gallery.length} →</Anchor>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="sm">
            {(gallery.some((e) => e.product !== m.examplesProduct)
              ? [...gallery.filter((e) => e.product === m.examplesProduct || !e.product).slice(0, 3), ...gallery.filter((e) => e.product && e.product !== m.examplesProduct).slice(0, 1)]
              : gallery.slice(0, 4)).map((e) => (
              <Card withBorder p="sm" key={e.id} component={Link} to={examplesPath(m.slug, e.id)}
                style={{ textDecoration: "none", color: "inherit" }} data-testid="module-example">
                <Text fw={600} size="sm">{e.name}</Text>
                <Text size="xs" c="dimmed" mt={2} mb={6} lineClamp={2}>{e.material}</Text>
                <div>{e.speciality.slice(0, 3).map((t) => <Tag key={t}>{t}</Tag>)}</div>
              </Card>
            ))}
          </SimpleGrid>
        </div>
      ) : m.product && (
        <Text size="sm" c="dimmed">Illustrative projects for {m.name} are coming.</Text>
      )}
    </Stack>
  );
}
